import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { printStationPending, printStationMarkPrinted, printStationReset } from "@/lib/api";

const POLL_INTERVAL_MS = 4000;          // how often to ask backend for new orders
const PRINT_DWELL_MS = 2500;            // time we leave the iframe mounted for window.print() to finish
const HISTORY_KEEP = 25;                // recent prints shown on screen
const DEDUPE_TTL_MS = 65 * 60 * 1000;   // keep ids in the local de-dup set just longer than the backend's 60-min window

/**
 * Naami Print Station — meant to run on a Windows desktop PC tucked under the
 * counter with the Epson connected via USB or Ethernet (as a normal Windows
 * printer). Launch Chrome with:
 *
 *     chrome.exe --kiosk --kiosk-printing https://<your-domain>/hub/print-station
 *
 * Behaviour:
 *  1. Polls /api/print-station/pending every 4s.
 *  2. For each unprinted paid order, mounts a hidden <iframe src="/print/{id}">.
 *     PrintSlips auto-fires window.print() on load. With --kiosk-printing,
 *     Chrome prints the iframe contents silently to the default printer.
 *  3. After the dwell timeout, calls POST /print-station/{id}/printed so we
 *     never print it twice, then removes the iframe.
 *  4. Items are printed sequentially (one in flight at a time) to keep the
 *     Epson from interleaving slips and to avoid Chrome merging print jobs.
 */
const HubPrintStation = () => {
    const navigate = useNavigate();
    const [connected, setConnected] = useState(false);
    const [lastPollAt, setLastPollAt] = useState(null);
    const [lastPrintAt, setLastPrintAt] = useState(null);
    const [currentOrder, setCurrentOrder] = useState(null);
    const [history, setHistory] = useState([]);
    const [error, setError] = useState(null);
    const [paused, setPaused] = useState(false);
    const inflightRef = useRef(false);     // true while an iframe is mounted + printing
    const printedIdsRef = useRef(new Map()); // id -> markedAt ms (pruned periodically)

    useEffect(() => {
        // Auth gate — reuse the same staff token the Hub uses
        try {
            const raw = localStorage.getItem("naami_staff");
            if (!raw) {
                navigate("/hub/login?next=/hub/print-station", { replace: true });
            }
        } catch {
            navigate("/hub/login?next=/hub/print-station", { replace: true });
        }
    }, [navigate]);

    const printOne = useCallback(async (order) => {
        if (printedIdsRef.current.has(order.id)) return;
        printedIdsRef.current.set(order.id, Date.now());
        inflightRef.current = true;
        setCurrentOrder(order);
        try {
            // Mount happens via React render of <iframe>. After dwell, mark printed.
            await new Promise((res) => setTimeout(res, PRINT_DWELL_MS));
            await printStationMarkPrinted(order.id);
            setLastPrintAt(new Date());
            setHistory((h) => [{ ...order, printed_at: new Date().toISOString() }, ...h].slice(0, HISTORY_KEEP));
        } catch (e) {
            // Don't block the queue on a single failure — log and let the next
            // poll pick it up (if not actually marked printed).
            setError(`Failed to mark ${order.order_number} as printed: ${e.message || e}`);
            printedIdsRef.current.delete(order.id);
        } finally {
            setCurrentOrder(null);
            inflightRef.current = false;
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        let timerId;

        const poll = async () => {
            if (cancelled || paused) {
                timerId = setTimeout(poll, POLL_INTERVAL_MS);
                return;
            }
            try {
                const r = await printStationPending(60);
                if (cancelled) return;
                setConnected(true);
                setLastPollAt(new Date());
                setError(null);
                // Prune the local de-dup map of entries older than the backend window
                const cutoff = Date.now() - DEDUPE_TTL_MS;
                for (const [id, t] of printedIdsRef.current) {
                    if (t < cutoff) printedIdsRef.current.delete(id);
                }
                // Process queue sequentially — only print when none in flight
                if (!inflightRef.current && r.pending?.length) {
                    const next = r.pending.find((o) => !printedIdsRef.current.has(o.id));
                    if (next) {
                        // fire-and-forget (UI will re-poll while this runs)
                        printOne(next);
                    }
                }
            } catch (e) {
                if (!cancelled) {
                    setConnected(false);
                    setError(`Poll failed: ${e.message || e}`);
                }
            } finally {
                if (!cancelled) timerId = setTimeout(poll, POLL_INTERVAL_MS);
            }
        };
        poll();
        return () => { cancelled = true; clearTimeout(timerId); };
    }, [paused, printOne]);

    const manualReprint = async (orderId) => {
        try {
            await printStationReset(orderId);
            printedIdsRef.current.delete(orderId);
        } catch (e) {
            setError(`Reprint failed: ${e.message || e}`);
        }
    };

    const formatAgo = (d) => {
        if (!d) return "—";
        const s = Math.floor((Date.now() - d.getTime()) / 1000);
        if (s < 5) return "just now";
        if (s < 60) return `${s}s ago`;
        if (s < 3600) return `${Math.floor(s / 60)}m ago`;
        return `${Math.floor(s / 3600)}h ago`;
    };

    return (
        <div className="min-h-screen bg-[#0a0a0a] text-[var(--text)] font-mono" data-testid="print-station-page">
            <header className="border-b border-[var(--border)] px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Link to="/hub" className="text-[10px] uppercase tracking-[0.22em] text-muted hover:text-gold">
                        ← naami<span className="text-gold">.</span> hub
                    </Link>
                    <h1 className="text-[14px] uppercase tracking-[0.22em]">
                        Print Station
                    </h1>
                </div>
                <div className="flex items-center gap-5 text-[11px]">
                    <span className="flex items-center gap-2" data-testid="ps-status">
                        <span className={`w-2 h-2 rounded-full ${connected ? "bg-emerald-500 animate-pulse" : "bg-[#ff5246]"}`} />
                        {connected ? "Connected" : "Disconnected"}
                    </span>
                    <span className="text-muted">Last poll: <span className="text-[var(--text)]" data-testid="ps-last-poll">{formatAgo(lastPollAt)}</span></span>
                    <span className="text-muted">Last print: <span className="text-gold" data-testid="ps-last-print">{formatAgo(lastPrintAt)}</span></span>
                    <span className="text-muted">Printed today: <span className="text-gold" data-testid="ps-count">{history.length}</span></span>
                    <button
                        type="button"
                        onClick={() => setPaused((p) => !p)}
                        className={`px-3 py-1.5 border text-[10px] uppercase tracking-[0.18em] ${
                            paused
                                ? "border-[#ff5246] text-[#ff5246]"
                                : "border-[var(--border)] text-muted hover:text-[var(--text)]"
                        }`}
                        data-testid="ps-pause-toggle"
                    >
                        {paused ? "▶ Resume" : "❚❚ Pause"}
                    </button>
                </div>
            </header>

            <main className="px-6 py-8 max-w-5xl mx-auto">
                {error && (
                    <div className="border border-[#ff5246] bg-[rgba(255,82,70,0.06)] px-4 py-3 mb-6 text-[12px] text-[#ff5246]" data-testid="ps-error">
                        {error}
                    </div>
                )}

                <div className="border border-[var(--border)] p-6 mb-6" data-testid="ps-now-printing">
                    <p className="eyebrow mb-3">// Now Printing</p>
                    {currentOrder ? (
                        <div className="flex items-center gap-4">
                            <div className="w-3 h-3 rounded-full bg-gold animate-pulse" />
                            <div className="flex-1">
                                <p className="text-[24px] font-display">#{currentOrder.order_number}</p>
                                <p className="text-[11px] uppercase tracking-[0.18em] text-muted mt-1">
                                    {currentOrder.order_type} · {currentOrder.customer_name || "—"}
                                </p>
                            </div>
                            <span className="text-[10px] uppercase tracking-[0.22em] text-gold">▸ Slip dropping…</span>
                        </div>
                    ) : (
                        <p className="text-muted text-[13px]">{paused ? "⏸ Paused" : "Waiting for next order…"}</p>
                    )}
                </div>

                <div>
                    <p className="eyebrow mb-3">// Recent Prints</p>
                    {history.length === 0 ? (
                        <p className="text-muted text-[13px] py-6 text-center border border-dashed border-[var(--border)]">
                            Nothing printed in this session yet.
                        </p>
                    ) : (
                        <div className="divide-y divide-[var(--border)] border border-[var(--border)]" data-testid="ps-history">
                            {history.map((o) => (
                                <div key={`${o.id}-${o.printed_at}`} className="px-4 py-3 flex items-center gap-4 text-[12px]">
                                    <span className="text-gold w-20">#{o.order_number}</span>
                                    <span className="text-muted uppercase tracking-[0.18em] text-[10px] w-24">{o.order_type}</span>
                                    <span className="flex-1 truncate">{o.customer_name || "—"}</span>
                                    <span className="text-muted text-[10px]">{formatAgo(new Date(o.printed_at))}</span>
                                    <button
                                        onClick={() => manualReprint(o.id)}
                                        className="text-[10px] uppercase tracking-[0.18em] text-muted hover:text-gold border border-[var(--border)] hover:border-gold px-2 py-1"
                                        data-testid={`ps-reprint-${o.order_number}`}
                                    >
                                        Reprint
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <details className="mt-10 text-[11px]">
                    <summary className="text-muted uppercase tracking-[0.22em] cursor-pointer hover:text-[var(--text)]">⚙ Setup instructions</summary>
                    <div className="mt-3 border border-[var(--border)] p-5 space-y-3 leading-relaxed">
                        <p>1. On the Windows PC, install Epson&apos;s <strong>Advanced Printer Driver (APD)</strong> from epson.com and set the TM-m30II as the <strong>default printer</strong>.</p>
                        <p>2. Print a Windows test page to confirm the Epson works as a regular printer.</p>
                        <p>3. Create a desktop shortcut with this target:</p>
                        <code className="block bg-[var(--surface-elevated)] p-2 text-[10px] break-all">
                            chrome.exe --kiosk --kiosk-printing --no-default-browser-check {window.location.origin}/hub/print-station
                        </code>
                        <p>4. Launch the shortcut. The browser opens full-screen, no dialogs.</p>
                        <p>5. Test: take an order from any device. Within ~5 seconds the Epson should print the slip automatically.</p>
                        <p className="text-muted">Tip: drop this shortcut into the Windows <em>Startup</em> folder so the print station boots automatically on power-on.</p>
                    </div>
                </details>
            </main>

            {/* Hidden iframe that does the actual printing. /print/{id} already auto-fires window.print(). */}
            {currentOrder && (
                <iframe
                    title="print-frame"
                    src={`/print/${currentOrder.id}`}
                    style={{ position: "fixed", width: 1, height: 1, opacity: 0, border: 0, pointerEvents: "none", left: -1000, top: -1000 }}
                    data-testid="ps-print-iframe"
                />
            )}
        </div>
    );
};

export default HubPrintStation;
